import { useState, useMemo, useCallback, type ReactNode } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnSizingState,
  type RowData,
} from "@tanstack/react-table"
import {
  HeartIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline"
import { ArrowDownSvg, ArrowTopSvg } from "~/ui-rtk/components/ui/svg/arrow"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import {
  InitiativeTypeDropdown,
  useInitiativeTypeManagement,
} from "~/ui-rtk/components/features/initiative-type"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import InlineEditCell from "./InlineEditCell"
import PinRowControl from "./PinRowControl"
import {
  describeDelivery,
  describeLock,
  describeRecentChange,
  describeStepCap,
  isLimitedByBudget,
  isUnderDelivering,
  limitedByBudgetTitle,
  lockBadge,
  primaryRecentChange,
  recentChangeBadge,
  summarizeRows,
} from "../budget-utils"
import {
  ANALYSIS_PERIOD_SELECT_OPTIONS,
  type ConfidenceTier,
  type RecentChange,
  type RecommendationRow,
} from "../types"

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: "left" | "center" | "right"
  }
}

const CHANNEL_BADGE_COLORS: Record<string, string> = {
  Meta: "bg-blue-600/30 text-blue-300",
  TikTok: "bg-pink-600/30 text-pink-300",
  Pinterest: "bg-rose-600/30 text-rose-300",
  "Google Ads": "bg-yellow-600/30 text-yellow-300",
  YouTube: "bg-red-600/30 text-red-300",
}

const CONFIDENCE_BADGE: Record<ConfidenceTier, { label: string; cls: string }> =
  {
    HIGH: { label: "High", cls: "bg-green-600/30 text-green-300" },
    MEDIUM: { label: "Medium", cls: "bg-yellow-600/30 text-yellow-300" },
    LOW: { label: "Low", cls: "bg-red-600/30 text-red-300" },
  }

function formatDateDisplay(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const COLUMN_SIZING_STORAGE_KEY = "budget-recommender-column-sizing"

/** Horizontal padding of a body cell (`px-3` on both sides), so a cell can
 *  bound its inner container to the column's size. */
const CELL_HORIZONTAL_PADDING_PX = 24

// Map a recommendation row into the rowData shape the shared initiative-type
// container expects. Campaign rows carry childAdsetIds so a change can cascade.
function toInitiativeRowData(row: RecommendationRow) {
  const isAdset = row.entityType === "adset"
  return {
    // Spread the row's window-aggregated Cube measures so the change modal can
    // project modeled revenue for a different initiative type on the frontend
    // (same computation as the DR/Brand ROAS figures). Explicit keys below win.
    ...(row.measures ?? {}),
    campaign_id: row.campaignId,
    ...(isAdset
      ? { ad_set_id: row.entityId, adset_name: row.entityName }
      : { campaign_name: row.entityName }),
    media_channel: row.channel,
    initiative_type:
      row.initiativeType !== "UNASSIGNED" ? row.initiativeType : "",
    modeled_revenue: String(row.modeledRevenuePeriod ?? 0),
    childAdsetIds: row.childAdsetIds,
  }
}

const columnHelper = createColumnHelper<RecommendationRow>()

/**
 * Tooltip for a labeled row: every recent change on it (with the size of a
 * budget change), and what the label means — a heads-up, not a lock.
 */
function recentChangesTitle(changes: RecentChange[]): string {
  return (
    `${changes.map(describeRecentChange).join(". ")}. ` +
    "Included like any other row; approving a change to it asks for an acknowledgment."
  )
}

/** Tooltip for an unchecked row's "as is" budget. */
function notIncludedTitle(row: RecommendationRow): string {
  return row.isActive
    ? "Not included: stays at its current budget, which counts toward the target"
    : "Not included: stays as is (inactive, not counted toward the target)"
}

/** Colors for the compact recent-change badge, by what happened. */
const RECENT_CHANGE_BADGE_CLASS: Record<RecentChange["reason"], string> = {
  budget: "text-amber-300 bg-amber-900/40 border-amber-700",
  "bid-target": "text-amber-300 bg-amber-900/40 border-amber-700",
  enabled: "text-amber-300 bg-amber-900/40 border-amber-700",
  "first-spend": "text-sky-300 bg-sky-900/40 border-sky-700",
}

interface RecommendationsTableProps {
  rows: RecommendationRow[]
  selectedIds: Set<string>
  editOverrides: Map<string, number>
  latestCompleteDate: string | null
  onToggleSelect: (entityId: string) => void
  onToggleSelectAll: () => void
  onEditBudget: (entityId: string, value: number) => void
  onChangePeriod: (entityId: string, days: number) => void
  onSavePeriod: (
    entityType: "campaign" | "adset",
    entityId: string,
    days: number,
  ) => void
  isPeriodDirty: (entityId: string) => boolean
  /** Shown instead of the table when `rows` is empty */
  emptyMessage?: ReactNode
}

export default function RecommendationsTable({
  rows,
  selectedIds,
  editOverrides,
  latestCompleteDate,
  onToggleSelect,
  onToggleSelectAll,
  onEditBudget,
  onChangePeriod,
  onSavePeriod,
  isPeriodDirty,
  emptyMessage,
}: RecommendationsTableProps) {
  const { getInitiativeTypeOptions, handleInitiativeTypeChange } =
    useInitiativeTypeManagement()
  // A locked row (pin or brand-search policy) is never selectable.
  const selectableRows = useMemo(
    () => rows.filter(r => r.category !== null && !r.lock),
    [rows],
  )
  const allSelected =
    selectableRows.length > 0 && selectedIds.size === selectableRows.length

  const [sorting, setSorting] = useState<SortingState>([
    { id: "sortPriority", desc: false },
    { id: "periodSpend", desc: true },
  ])

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_SIZING_STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  const handleColumnSizingChange = useCallback(
    (
      updater:
        | ColumnSizingState
        | ((prev: ColumnSizingState) => ColumnSizingState),
    ) => {
      setColumnSizing(prev => {
        const next = typeof updater === "function" ? updater(prev) : updater
        localStorage.setItem(COLUMN_SIZING_STORAGE_KEY, JSON.stringify(next))
        return next
      })
    },
    [],
  )

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 48,
        minSize: 48,
        maxSize: 48,
        enableSorting: false,
        enableResizing: false,
        header: () => (
          <Checkbox
            checked={allSelected}
            onChange={onToggleSelectAll}
            className="checkbox-sm"
          />
        ),
        cell: info => {
          const row = info.row.original
          if (!row.category) return <div />
          // A locked row stays unchecked until someone unpins it (or, for
          // the brand-search policy, changes the policy).
          if (row.lock) {
            return (
              <Checkbox
                checked={false}
                disabled
                className="checkbox-sm cursor-not-allowed opacity-50"
                title={`${describeLock(row.lock)} Not included: stays at its current budget, which counts toward the target.`}
              />
            )
          }
          // Every categorized row is selectable, labeled ones included: a
          // recent change is a heads-up (acknowledged on Review), not a lock.
          // Unchecked, the row stays at its current budget and, when active,
          // commits that budget to the allocation pool.
          return (
            <Checkbox
              checked={selectedIds.has(row.entityId)}
              onChange={() => onToggleSelect(row.entityId)}
              className="checkbox-sm"
              title={
                selectedIds.has(row.entityId)
                  ? "Included in this budget update"
                  : row.isActive
                    ? "Not included: stays at its current budget, which counts toward the target"
                    : "Not included: stays as is (inactive, not counted toward the target)"
              }
            />
          )
        },
      }),
      columnHelper.accessor("entityName", {
        id: "entityName",
        header: "Entity",
        size: 250,
        minSize: 120,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const row = info.row.original
          return (
            <div
              className="flex items-center gap-2 min-w-0"
              // Bound the content to the column's size. This is an auto-layout
              // table, where a nowrap name is otherwise the column's minimum
              // content width: long names blew the column (and the table) out
              // no matter what the header width or the drag handle said.
              style={{
                width: info.column.getSize() - CELL_HORIZONTAL_PADDING_PX,
              }}
            >
              {row.category === "BRAND" ? (
                <HeartIcon className="w-4 h-4 text-pink-400 shrink-0" />
              ) : row.category === "DR" ? (
                <CurrencyDollarIcon className="w-4 h-4 text-american-green shrink-0" />
              ) : (
                <div className="w-4 h-4 shrink-0" />
              )}
              <span
                className="text-white text-sm truncate"
                title={row.entityName}
              >
                {row.entityName}
              </span>
              <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-basic-black rounded shrink-0">
                {row.entityType}
              </span>
              {row.hasUncategorizedAdsets && (
                <span title="Some spending ad sets under this campaign have no initiative type, and the campaign has no campaign-level type for them to inherit">
                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-400 shrink-0" />
                </span>
              )}
              {(() => {
                const change = primaryRecentChange(row)
                if (!change || !row.recentChanges) return null
                return (
                  <span
                    title={recentChangesTitle(row.recentChanges)}
                    className={cn(
                      "text-xs px-1.5 py-0.5 border rounded shrink-0",
                      RECENT_CHANGE_BADGE_CLASS[change.reason],
                    )}
                  >
                    {recentChangeBadge(change)}
                    {row.recentChanges.length > 1 &&
                      ` +${row.recentChanges.length - 1}`}
                  </span>
                )
              })()}
              {row.lock && (
                <span
                  title={describeLock(row.lock)}
                  className="text-xs text-violet-300 px-1.5 py-0.5 bg-violet-900/40 border border-violet-700 rounded shrink-0"
                >
                  {lockBadge(row.lock)}
                </span>
              )}
              {isLimitedByBudget(row) && (
                <span
                  title={limitedByBudgetTitle(row)}
                  className="text-xs text-emerald-300 px-1.5 py-0.5 bg-emerald-900/40 border border-emerald-700 rounded shrink-0"
                >
                  Limited by budget
                </span>
              )}
              {row.platformStatus === "INACTIVE" && (
                <span
                  title="Paused on the platform: not spending, so its budget does not count toward the target"
                  className="text-xs text-gray-400 px-1.5 py-0.5 bg-gray-800 border border-gray-700 rounded shrink-0"
                >
                  Paused
                </span>
              )}
              {isUnderDelivering(row) && (
                <span
                  title={describeDelivery(row)}
                  className="text-xs text-yellow-300 px-1.5 py-0.5 bg-yellow-900/40 border border-yellow-700 rounded shrink-0"
                >
                  Under-delivering
                </span>
              )}
              {row.category && <PinRowControl row={row} />}
            </div>
          )
        },
      }),
      columnHelper.accessor("initiativeType", {
        id: "initiativeType",
        header: "Initiative Type",
        size: 250,
        minSize: 150,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const row = info.row.original
          const rowData = toInitiativeRowData(row)
          return (
            <InitiativeTypeDropdown
              value={
                row.initiativeType !== "UNASSIGNED"
                  ? row.initiativeType
                  : undefined
              }
              options={getInitiativeTypeOptions(rowData)}
              onChange={value => handleInitiativeTypeChange(value, rowData)}
              onStopPropagation={e => e.stopPropagation()}
            />
          )
        },
      }),
      columnHelper.accessor("channel", {
        id: "channel",
        header: "Channel",
        size: 110,
        minSize: 80,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const badgeColor =
            CHANNEL_BADGE_COLORS[info.getValue()] ??
            "bg-gray-600/30 text-gray-300"
          return (
            <span
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-medium",
                badgeColor,
              )}
            >
              {info.getValue()}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: "dataThrough",
        header: "Data Through",
        size: 120,
        minSize: 90,
        enableSorting: false,
        enableResizing: false,
        cell: info => {
          const rowDate = info.row.original.dataThroughDate
          if (!rowDate) {
            return <span className="text-sm text-gray-500">--</span>
          }
          // YouTube Demand Gen rows anchor to brand value freshness, which
          // lags the page-level (spend-based) complete date
          const isLagged =
            latestCompleteDate !== null && rowDate < latestCompleteDate
          return (
            <span
              title={
                isLagged
                  ? "Analysis window ends at the latest day with complete YouTube Analytics brand value data, which lands a few days after Google Ads spend data"
                  : undefined
              }
              className={cn(
                "text-sm",
                isLagged ? "text-yellow-400" : "text-gray-300",
              )}
            >
              {formatDateDisplay(rowDate)}
            </span>
          )
        },
      }),
      columnHelper.accessor("analysisPeriodDays", {
        id: "analysisPeriodDays",
        header: "Analysis Period",
        size: 150,
        minSize: 120,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const row = info.row.original
          return (
            <div
              className="flex items-center gap-1"
              onClick={e => e.stopPropagation()}
            >
              <Select
                options={ANALYSIS_PERIOD_SELECT_OPTIONS}
                value={row.analysisPeriodDays}
                onChange={e =>
                  onChangePeriod(row.entityId, Number(e.target.value))
                }
                classes={{
                  // Sized by its own padding (h-auto + py-1) rather than a fixed
                  // h-7, which used to clip the selected value. That clipping was
                  // never a font-metrics problem -- it is the @tailwindcss/forms
                  // base padding explained in Select. The shared control zeroes
                  // that now; this py-1 still wins via tw-merge, so the row keeps
                  // its compact sizing.
                  select:
                    "select-xs min-h-0 h-auto w-auto py-1 text-xs leading-tight items-center",
                }}
              />
              {isPeriodDirty(row.entityId) && (
                <button
                  type="button"
                  title="Save as default analysis period for this row"
                  className="btn btn-ghost btn-xs px-1 text-blue-300 hover:text-blue-200"
                  onClick={() =>
                    onSavePeriod(
                      row.entityType,
                      row.entityId,
                      row.analysisPeriodDays,
                    )
                  }
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor("periodSpend", {
        id: "periodSpend",
        header: "Period Spend",
        size: 120,
        minSize: 80,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => (
          <span className="text-sm">
            {formatNumber(info.getValue(), { currency: true })}
          </span>
        ),
      }),
      columnHelper.accessor("avgDailySpend", {
        id: "avgDailySpend",
        header: "Avg Daily Spend",
        size: 140,
        minSize: 100,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => {
          const row = info.row.original
          if (!row.category) {
            return <span className="text-sm text-gray-500">--</span>
          }
          return (
            <span className="text-sm">
              {formatNumber(info.getValue(), { currency: true })}
              {row.daysWithSpend < row.analysisPeriodDays && (
                <span className="text-yellow-400 text-xs ml-1">
                  ({row.daysWithSpend}d)
                </span>
              )}
            </span>
          )
        },
      }),
      columnHelper.accessor("drRoas", {
        id: "drRoas",
        header: "DR ROAS",
        size: 100,
        minSize: 70,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info =>
          info.row.original.category ? (
            <span className="text-sm">{info.getValue().toFixed(2)}x</span>
          ) : (
            <span className="text-sm text-gray-500">--</span>
          ),
      }),
      columnHelper.accessor("brandRoas", {
        id: "brandRoas",
        header: "Brand ROAS",
        size: 100,
        minSize: 70,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info =>
          info.row.original.category ? (
            <span className="text-sm">{info.getValue().toFixed(2)}x</span>
          ) : (
            <span className="text-sm text-gray-500">--</span>
          ),
      }),
      columnHelper.accessor(row => row.estimatedConversions ?? -1, {
        id: "confidence",
        header: "Confidence",
        size: 110,
        minSize: 80,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const row = info.row.original
          if (!row.category) {
            return <span className="text-sm text-gray-500">--</span>
          }
          if (row.confidence === null) {
            return (
              <span
                title="Set Avg Order Value to estimate confidence"
                className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-600/30 text-gray-300"
              >
                N/A
              </span>
            )
          }
          const badge = CONFIDENCE_BADGE[row.confidence]
          return (
            <span
              title={`≈${Math.round(row.estimatedConversions ?? 0)} est. conversions over ${row.analysisPeriodDays}d`}
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-medium",
                badge.cls,
              )}
            >
              {badge.label}
            </span>
          )
        },
      }),
      columnHelper.accessor("currentDailyBudget", {
        id: "currentDailyBudget",
        header: "Current Budget",
        size: 130,
        minSize: 90,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => (
          <span className="text-sm">
            {formatNumber(info.getValue(), { currency: true })}
          </span>
        ),
      }),
      columnHelper.accessor("recommendedBudget", {
        id: "recommendedBudget",
        header: "Recommended",
        size: 120,
        minSize: 90,
        enableSorting: true,
        enableResizing: true,
        meta: { align: "right" },
        cell: info => {
          if (!info.row.original.category) {
            return <span className="text-sm text-gray-500">--</span>
          }
          return (
            <span className="text-sm text-gray-300">
              {formatNumber(info.getValue(), { currency: true })}
            </span>
          )
        },
      }),
      columnHelper.accessor("adjustedBudget", {
        id: "adjustedBudget",
        header: "Adjusted Budget",
        size: 160,
        minSize: 120,
        enableSorting: true,
        enableResizing: true,
        cell: info => {
          const row = info.row.original
          if (!row.category) {
            return (
              <span className="text-sm text-gray-500">Assign type first</span>
            )
          }
          if (!selectedIds.has(row.entityId) || row.lock) {
            // Unchecked (or locked): the row keeps its current budget. The
            // raw recommendation stays visible in the Recommended column;
            // check the row to bring it into the reallocation.
            return (
              <span
                className="text-sm text-gray-500"
                title={
                  row.lock ? describeLock(row.lock) : notIncludedTitle(row)
                }
              >
                {formatNumber(row.currentDailyBudget, { currency: true })}
                <span className="ml-1 text-[10px] uppercase tracking-wider">
                  as is
                </span>
              </span>
            )
          }
          if (row.shouldPause) {
            return (
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-states-error/20 text-states-error">
                Pause
              </span>
            )
          }
          const cell = (
            <InlineEditCell
              value={row.adjustedBudget}
              editedValue={editOverrides.get(row.entityId)}
              onConfirm={val => onEditBudget(row.entityId, val)}
            />
          )
          if (!row.stepCapped) return cell
          return (
            <div className="flex items-center gap-1">
              {cell}
              <span
                title={describeStepCap(row)}
                className="text-[10px] uppercase tracking-wider text-sky-300 shrink-0"
              >
                capped
              </span>
            </div>
          )
        },
      }),
      columnHelper.accessor(
        row => {
          const effectiveBudget =
            editOverrides.get(row.entityId) ?? row.adjustedBudget
          if (!row.category || row.shouldPause) return null
          return row.currentDailyBudget > 0
            ? ((effectiveBudget - row.currentDailyBudget) /
                row.currentDailyBudget) *
                100
            : 0
        },
        {
          id: "percentChange",
          header: "% Change",
          size: 100,
          minSize: 70,
          enableSorting: true,
          enableResizing: true,
          meta: { align: "right" },
          sortUndefined: "last",
          cell: info => {
            const row = info.row.original
            if (!row.category) {
              return <span className="text-sm text-gray-500">--</span>
            }
            if (row.shouldPause) {
              return (
                <span className="text-sm font-medium text-states-error">
                  Pause
                </span>
              )
            }
            const pct = info.getValue() as number
            return (
              <span
                className={cn(
                  "text-sm font-medium",
                  pct > 0
                    ? "text-american-green"
                    : pct < 0
                      ? "text-states-error"
                      : "text-gray-400",
                )}
              >
                {pct > 0 ? "+" : ""}
                {pct.toFixed(1)}%
              </span>
            )
          },
        },
      ),
      // Hidden column used for default sort order
      columnHelper.accessor(
        row => (row.category === null ? 2 : row.category === "BRAND" ? 0 : 1),
        {
          id: "sortPriority",
          header: "",
          enableSorting: true,
          enableResizing: false,
        },
      ),
    ],
    [
      allSelected,
      selectedIds,
      editOverrides,
      latestCompleteDate,
      onToggleSelect,
      onToggleSelectAll,
      onEditBudget,
      getInitiativeTypeOptions,
      handleInitiativeTypeChange,
      onChangePeriod,
      onSavePeriod,
      isPeriodDirty,
    ],
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnSizing,
      columnVisibility: { sortPriority: false },
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onColumnSizingChange: handleColumnSizingChange,
    enableMultiSort: true,
  })

  // Column totals over the rows shown (assigned entities only), honoring edits
  const summary = useMemo(
    () => summarizeRows(rows, editOverrides),
    [rows, editOverrides],
  )

  const renderSummaryCell = useCallback(
    (colId: string) => {
      switch (colId) {
        case "entityName":
          return "Total"
        case "periodSpend":
          return <span title="Periods differ per row">—</span>
        case "avgDailySpend":
          return formatNumber(summary.totalAvgDaily, { currency: true })
        case "currentDailyBudget":
          return formatNumber(summary.totalCurrent, { currency: true })
        case "recommendedBudget":
          return formatNumber(summary.totalRawRecommended, { currency: true })
        case "adjustedBudget":
          return formatNumber(summary.totalAdjusted, { currency: true })
        case "percentChange":
          return (
            <span
              className={cn(
                summary.aggPctChange > 0
                  ? "text-american-green"
                  : summary.aggPctChange < 0
                    ? "text-states-error"
                    : "text-gray-400",
              )}
            >
              {summary.aggPctChange > 0 ? "+" : ""}
              {summary.aggPctChange.toFixed(1)}%
            </span>
          )
        default:
          return null
      }
    },
    [summary],
  )

  if (rows.length === 0) {
    return (
      <div className="border border-basic-blue rounded-lg p-8 text-center text-gray-400">
        {emptyMessage ??
          "No campaigns or ad sets with spend found for the last 30 days."}
      </div>
    )
  }

  return (
    <div className="border border-basic-blue rounded-lg overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isSorted = header.column.getIsSorted()
                const canSort = header.column.getCanSort()
                const align = header.column.columnDef.meta?.align ?? "left"

                return (
                  <th
                    key={header.id}
                    className={cn(
                      "relative px-3 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-basic-dark-blue group select-none",
                      canSort && "cursor-pointer",
                      align === "right" && "text-right",
                    )}
                    style={{ width: header.getSize() }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div
                      className={cn(
                        "flex items-center gap-1",
                        align === "right" && "justify-end",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {isSorted === "desc" && <ArrowDownSvg size={14} />}
                      {isSorted === "asc" && <ArrowTopSvg size={14} />}
                      {!isSorted && canSort && (
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowDownSvg size={14} />
                        </span>
                      )}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={e => e.stopPropagation()}
                        onDoubleClick={e => {
                          e.stopPropagation()
                          header.column.resetSize()
                        }}
                        title="Drag to resize · double-click to reset"
                        className="hover:bg-blue-400/50"
                        style={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          height: "100%",
                          width: "6px",
                          cursor: "col-resize",
                          userSelect: "none",
                          touchAction: "none",
                          zIndex: 10,
                          background: header.column.getIsResizing()
                            ? "#60a5fa"
                            : "transparent",
                          transition: "background 0.2s",
                        }}
                      />
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className="border-t border-basic-blue hover:bg-system-blue-800/50 transition-colors"
            >
              {row.getVisibleCells().map(cell => {
                const align = cell.column.columnDef.meta?.align ?? "left"
                return (
                  <td
                    key={cell.id}
                    className={cn(
                      "px-3 py-2",
                      align === "right" && "text-right",
                    )}
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-system-blue-800 border-t border-basic-blue">
            {table
              .getHeaderGroups()[0]
              .headers.filter(h => h.column.getIsVisible())
              .map(header => {
                const colId = header.column.id
                const align = header.column.columnDef.meta?.align ?? "left"
                return (
                  <td
                    key={colId}
                    className={cn(
                      "px-3 py-3 text-sm font-semibold text-white",
                      align === "right" && "text-right",
                    )}
                    style={{ width: header.getSize() }}
                  >
                    {renderSummaryCell(colId)}
                  </td>
                )
              })}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
